import { surfaceField } from './from-base-encounter-media';
import { fromBaseRecord, fromBaseUuid, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import { phpJsonInt, phpJsonString } from './php-json-values';
import { phpJsonFloat } from './php-decimal-values';

const prefix = 'encounter_exploration_state_';
const columns = ['private', 'deleted', 'created_by_id', 'updated_by_id', 'id', 'encounter_id', 'team_id',
    'geometry_revision', 'cell_size_ft', 'revision', 'data'];
const schema = 'ft.ft3d.team-exploration-state.v1';

/** Normalize a held/raw row once before giving the stateless geometry planner its inputs. */
export function explorationCommandState(raw: Row): { id: string; revision: number; cellKeys: string[] } {
    const row = compileEncounterExplorationStateRow(raw);
    return { id: phpJsonString(row[prefix + 'id']), revision: Number(row[prefix + 'revision']),
        cellKeys: (row[prefix + 'data'] as { cellKeys: string[] }).cellKeys };
}

/** The all-team overlay is a structural section; its rows use the native surface readers. */
export function heldExplorationStates(encounter: Row): Row[] {
    const section = encounter.encounter_exploration_states_arr;
    return section && typeof section === 'object' ? Object.values(section).filter(fromBaseRecord) : [];
}

/** Adopt only newer team rows, preserving every unrelated held row and lean empty overlays. */
export function applyExplorationStateRows(encounter: Row, rows: readonly Row[]): Row[] {
    const held = new Map(heldExplorationStates(encounter).map(row =>
        [phpJsonString(surfaceField(row, prefix + 'team_id')).toUpperCase(), row] as const));
    const changed: Row[] = [];
    for (const raw of rows) {
        const row = compileEncounterExplorationStateRow(raw);
        const team = phpJsonString(row[prefix + 'team_id']).toUpperCase();
        if (!fromBaseUuid(team)) continue;
        const old = held.get(team);
        if (old && Number(surfaceField(old, prefix + 'revision') ?? 0) >= Number(row[prefix + 'revision'])) continue;
        if (!fromBaseRecord(encounter.encounter_exploration_states_arr)) encounter.encounter_exploration_states_arr = {};
        (encounter.encounter_exploration_states_arr as Row)[team] = row;
        held.set(team, row);
        changed.push(row);
    }
    return changed;
}

/** build_encounter_exploration_state_arr + the all-team overlay, without a Redis sidecar. */
export function compileEncounterExplorationFromBase(id: string, rows?: readonly Row[]): Row | [] {
    if (!Array.isArray(rows)) throw new FromBaseStageMissing('encounter-exploration-input', id);
    const states = new Map<string, Row>();
    for (const row of rows) {
        const read = (name: string): unknown => surfaceField(row, prefix + name) ?? null;
        if (String(read('encounter_id')).toUpperCase() !== id.toUpperCase() || phpJsonInt(read('deleted')) !== 0)
            throw new TypeError('Invalid exploration encounter membership/deletion');
        const team = phpJsonString(read('team_id')).toUpperCase();
        if (!fromBaseUuid(team)) continue; // Native overlay ignores malformed team ids.
        if (states.has(team)) throw new TypeError(`Duplicate exploration team: ${team}`);
        states.set(team, compileEncounterExplorationStateRow(row));
    }
    return states.size ? Object.fromEntries([...states.entries()].sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0)) : [];
}

/** Standalone native row constructor; the overlay separately ignores non-UUID teams. */
export function compileEncounterExplorationStateRow(row: Row): Row {
    const read = (name: string): unknown => surfaceField(row, prefix + name) ?? null;
    const state: Row = Object.fromEntries(columns.map(name => [prefix + name, read(name)]));
    for (const name of ['private', 'deleted']) state[prefix + name] = phpJsonInt(read(name));
    state[prefix + 'revision'] = Math.max(0, phpJsonInt(read('revision')));
    state[prefix + 'cell_size_ft'] = phpJsonFloat(read('cell_size_ft'));
    let data = read('data');
    if (typeof data === 'string' && data !== '') { try { data = JSON.parse(data); } catch { data = null; } }
    const keys = fromBaseRecord(data) && data.schema === schema && data.cellKeys !== null && typeof data.cellKeys === 'object'
        ? Object.values(data.cellKeys).map(value => phpJsonString(value).trim().toLowerCase()).filter(key => /^[a-f0-9]{64}$/.test(key)) : [];
    state[prefix + 'data'] = { schema, cellKeys: [...new Set(keys)].sort() };
    return state;
}
